import { DOCUMENT } from '@angular/common';
import { RendererFactory2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Product } from '../product/product.types';
import { ProductsService } from '../product/products.service';
import { CanonicalService } from '../shared/canonical.service';

export const productResolver: ResolveFn<Product | null> = (route): Observable<Product | null> => {
  const productsService = inject(ProductsService);
  const canonicalService = inject(CanonicalService);
  const title = inject(Title);
  const meta = inject(Meta);
  const document = inject(DOCUMENT);
  const renderer = inject(RendererFactory2).createRenderer(null, null);

  const seoUrl = `${route.params['category']}/${route.params['subcategory']}/${route.params['slug']}`;

  return productsService.getProductByUrl(seoUrl, true, false).pipe(
    tap((product: Product | null) => {
      if (!product) return;
      applyProductSeo(product as any, { title, meta, document, renderer, canonicalService });
    }),
    catchError(() => of(null))
  );
};

interface SeoDeps {
  title: Title;
  meta: Meta;
  document: Document;
  renderer: ReturnType<RendererFactory2['createRenderer']>;
  canonicalService: CanonicalService;
}

function applyProductSeo(product: any, deps: SeoDeps): void {
  const { title, meta, document, renderer, canonicalService } = deps;
  const appName = environment.application.name;
  const baseURL = environment.config.seo.baseURL;

  const pageTitle = `${product.name} | ${appName}`;
  title.setTitle(pageTitle);
  meta.updateTag({ property: 'og:title', content: pageTitle });
  meta.updateTag({ name: 'twitter:title', content: pageTitle });

  const pageUrl = `${baseURL}shop/${product.productUrl || ''}`;
  meta.updateTag({ property: 'og:url', content: pageUrl });
  meta.updateTag({ name: 'twitter:url', content: pageUrl });

  const rawDescription = product.description?.short || product.teaserDescription || '';
  const description = rawDescription.replace(/<[^>]*>/g, '').substring(0, 160);
  if (description) canonicalService.setPageDescription(description);

  const imageUrl = product.hrImg || product.img;
  if (imageUrl) canonicalService.setPageImage(imageUrl);

  canonicalService.setPageType('product');

  setProductJsonLd(product, { document, renderer, canonicalService });
}

function setProductJsonLd(
  product: any,
  deps: { document: Document; renderer: SeoDeps['renderer']; canonicalService: CanonicalService }
): void {
  const { document, renderer, canonicalService } = deps;

  const existing = document.head.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((script) => {
    if (script.textContent?.includes('"@type":"Product"')) {
      renderer.removeChild(document.head, script);
    }
  });

  const brandName = environment.application.name;
  const absoluteImage = canonicalService.getAbsoluteImageUrl(product.hrImg || product.img);
  const rawDescription = (product.description?.short || product.teaserDescription || '')
    .replace(/<[^>]*>/g, '')
    .substring(0, 5000);

  const price = product.price ?? product.originalPrice ?? 0;
  const isInStock = product.stock == null ? true : Number(product.stock) > 0;

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name,
    'description': rawDescription,
    'image': absoluteImage,
    'sku': String(product.id),
    'brand': {
      '@type': 'Brand',
      'name': brandName
    },
    'offers': {
      '@type': 'Offer',
      'price': price,
      'priceCurrency': 'USD',
      'availability': isInStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      'seller': {
        '@type': 'Organization',
        'name': brandName
      }
    }
  };

  if (product.category?.name) jsonLd['category'] = product.category.name;

  const script: HTMLScriptElement = renderer.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(jsonLd);
  renderer.appendChild(document.head, script);
}
